import { Inject, Injectable } from "@angular/core";
import { MessageSender, SatisfactionEmoji, Suggestion } from "app/chat/incoming";
import { AppParentService, LockUtil } from "app/core";
import { CONVERSATION_LOCK } from "../di-tokens";
import { OutgoingMessageFactory } from "./outgoing-message.factory";
import { EvaluationResult, OutgoingMessage, ScoreFormResult } from "./outgoing-message.model";

/** Service to send messages to the server */
@Injectable({ providedIn: "root" })
export class OutgoingMessageService {
  private hasEngaged = false;

  constructor(
    private readonly messageSender: MessageSender,
    private readonly outgoingMessageFactory: OutgoingMessageFactory,
    @Inject(CONVERSATION_LOCK) private readonly conversationLock: LockUtil,
    private readonly appParentService: AppParentService
  ) {}

  private sendMessageWithLocking(message: OutgoingMessage) {
    this.conversationLock.lock();
    this.messageSender.send(message);
  }

  /** Emit analytics for first engagement. */
  private emitEngagementAndMessageSent(): void {
    if (!this.hasEngaged) {
      this.hasEngaged = true;
      this.appParentService.notifyUserEngaged();
    }

    this.appParentService.notifyMessageSent();
  }

  public sendTextMessage(text: string) {
    const message = this.outgoingMessageFactory.makeTextMessage(text);
    this.sendMessageWithLocking(message);
    this.emitEngagementAndMessageSent();
    return message;
  }

  public sendIntentionMessage(intention: string) {
    const message = this.outgoingMessageFactory.makeIntentionMessage(intention);
    this.sendMessageWithLocking(message);
    return message;
  }

  public sendAcceptedSuggestionMessage(suggestion: Suggestion) {
    const message = this.outgoingMessageFactory.makeAcceptedSuggestionMessage(suggestion);
    this.sendMessageWithLocking(message);
    this.emitEngagementAndMessageSent();
    return message;
  }

  public sendEmojiSatisfactionMessage(emoji: SatisfactionEmoji) {
    const message = this.outgoingMessageFactory.makeSatisfactionEmojiMessage(emoji);
    this.sendMessageWithLocking(message);
    return message;
  }

  public sendSatisfactionCommentMessage(text: string, intention: string) {
    const message = this.outgoingMessageFactory.makeSatisfactionCommentMessage(text, intention);
    this.sendMessageWithLocking(message);
    return message;
  }

  public sendEvaluationMessage(evaluation: EvaluationResult) {
    const outgoingEvaluation =
      this.outgoingMessageFactory.makeEvaluationOutgoingMessage(evaluation);
    this.messageSender.sendEvaluation(outgoingEvaluation, evaluation.message_id);
    return outgoingEvaluation;
  }

  public sendEvaluationExplanationsMessage(evaluation: EvaluationResult) {
    const outgoingEvaluation =
      this.outgoingMessageFactory.makeEvaluationOutgoingMessage(evaluation);
    this.messageSender.sendEvaluationExplanations(outgoingEvaluation, evaluation.message_id);
    return outgoingEvaluation;
  }

  public sendScoreForm(scoreForm: ScoreFormResult) {
    const message = this.outgoingMessageFactory.makeScoreFormOutgoingMessage(scoreForm);
    this.messageSender.send(message);
    return message;
  }

  public cancelScoreForm() {
    const message = this.outgoingMessageFactory.makeGoToOutgoingMessage("evaluation_rebond");
    this.sendMessageWithLocking(message);
    return message;
  }
}
